import { Injectable, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { DERStepService } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/der-step/der-step.service';
import { QPIService } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/qpi-step/services/qpi.service';
import { StepStatusEnum } from '@lib/enums/step-status.enum';
import { FileDocument } from '@lib/models/file-document.model';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { RecueilService } from '@lib/services/recueil.service';
import { ReferenceDataService } from '@lib/services/reference-data.service';
import { sortByCustomOrder } from '@lib/utils/app-utils';
import {
  EtatDer,
  EtatQpi,
  EtatRecueil,
  FileMetadata,
  GedDocumentType,
  Investisseur,
  SignataireType,
} from 'projects/minotaure-lib/src/public-api';
import { DocumentForm, SelectedSignataire, SignataireForm, SignatureForm } from '../models/signature.form.model';

interface DocumentLoadingRule {
  shouldLoad: () => boolean;
  load: () => Promise<FileDocument | null>;
  shouldGenerateFakeDocument: () => boolean;
  generateFakeDocument: () => FileDocument;
  documentType: GedDocumentType;
  stepStatus?: StepStatusEnum;
  dateReference?: Date;
  investisseurIdToDisable?: number;
  investisseurId?: number;
  disabledIfSigned?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SignatureRulesService {
  private readonly investisseurService = inject(InvestisseurService);
  private readonly recueilService = inject(RecueilService);
  private readonly derService = inject(DERStepService);
  private readonly qpiService = inject(QPIService);

  private readonly pendingRules = new Map<string, DocumentLoadingRule>();
  private pendingRuleSequence = 0;

  public investisseur1Id = computed<number>(() => this.investisseurService.investisseurSig()?.id);
  public investisseur2Id = computed<number>(() => this.investisseurService.investisseurConjointSig()?.id);
  readonly isCouple = computed(() => !!this.investisseur2Id());
  readonly coupleIds = computed<number[]>(() => [this.investisseur1Id(), this.investisseur2Id()].filter(Boolean) as number[]);

  readonly errorTooltip = signal<string>('');

  //#region Document loading rules
  public async getDocuments(allDocuments: FileDocument[], splitRecueilByInvestisseur: boolean = false): Promise<FileDocument[]> {
    const invQPI = this.investisseur1Id() ? await this.qpiService.getQPIResults(this.investisseur1Id()) : null;
    const conjointQPI = this.investisseur2Id() ? await this.qpiService.getQPIResults(this.investisseur2Id()) : null;
    await this.ensureQPIStateBeforeLoadingDocuments();

    const inv = this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur;
    const conjoint = this.investisseurService.investisseurConjointSig()?.avancementConnaissanceClientInvestisseur;

    const isIn = <T>(value: T | undefined, allowed: T[]) => value !== undefined && allowed.includes(value);

    const flags = {
      inv: {
        derToComplete: inv?.derEtat === EtatDer.ACompleter,
        recueilToComplete: inv?.recueilEtat === EtatRecueil.ACompleter || !inv?.isRecueilFormCompleted,
        qpiAlreadySigned: isIn(inv?.qpiEtat, [EtatQpi.Signe, EtatQpi.SigneNouveauASigner]),
      },
      conjoint: {
        derToComplete: conjoint?.derEtat === EtatDer.ACompleter,
        recueilToComplete: conjoint?.recueilEtat === EtatRecueil.ACompleter || !conjoint?.isRecueilFormCompleted,
        qpiAlreadySigned: isIn(conjoint?.qpiEtat, [EtatQpi.Signe, EtatQpi.SigneNouveauASigner]),
      },
    };

    // recueilEtat/recueildocumentID suivent l'avancement global de l'investisseur (le pointeur "document actuel"),
    // pas celui d'une variante (couple/solo) précise : dès qu'un investisseur signe N'IMPORTE QUELLE variante,
    // son recueilEtat passe à Signe et recueildocumentID pointe vers ce dernier fichier signé, quel qu'il soit.
    // On recherche donc dans TOUT l'historique GED des deux investisseurs, variante par variante, si un document
    // a réellement été signé — ainsi une variante signée puis "dépassée" par une signature plus récente (ex :
    // solo signé, puis couple signé ensuite) reste correctement marquée comme signée elle aussi.
    const recueilSignedFiles = this.isCouple() ? await this.recueilService.getSignedRecueilFiles() : [];

    const idsEqual = (a?: number[] | null, b?: number[]) => !!a && !!b && a.length === b.length && a.every(id => b!.includes(id));
    const signedFileForVariant = (ids: number[]): FileMetadata | undefined =>
      recueilSignedFiles.find(f => idsEqual(f.investisseurIds, ids));
    const recueilVariantStatus = (ids: number[], etat: EtatRecueil | undefined | null): StepStatusEnum => {
      const mapped = ReferenceDataService.mapRecueilState(etat as EtatRecueil);
      if (mapped !== StepStatusEnum.Signed && mapped !== StepStatusEnum.Signing) return mapped;
      return signedFileForVariant(ids) ? mapped : StepStatusEnum.ToSign;
    };
    const signedFileDate = (ids: number[]): Date | undefined => {
      const f = signedFileForVariant(ids);
      const date = f?.dateDeDerniereModification ?? f?.dateDeCreation;
      return date ? new Date(date) : undefined;
    };

    // Set status for loaded docs
    for (const doc of allDocuments) {
      doc.disabledIfSigned = false;
      const isInv1 = doc.investisseurIds?.includes(this.investisseur1Id()!);
      const isInv2 = doc.investisseurIds?.includes(this.investisseur2Id());
      const current = isInv1 ? inv : isInv2 ? conjoint : undefined;
      if (!current || !doc.documentType) continue;

      switch (doc.documentType) {
        case GedDocumentType.Der:
          doc.stepStatus = ReferenceDataService.mapDERState(current?.derEtat as EtatDer);
          doc.dateReference = current?.derReferenceDate;
          break;
        case GedDocumentType.RecInfo: {
          const ids = doc.investisseurIds ?? [];
          // Documents passed in by the caller (e.g. the solo Recueil viewer's send-to-signature button)
          // must be disambiguated the same way regardless of splitRecueilByInvestisseur, since recueilEtat
          // alone can't tell a signed couple variant apart from an unsigned solo one (see comment above).
          const useVariantStatus = this.isCouple();
          doc.stepStatus = useVariantStatus
            ? recueilVariantStatus(ids, current?.recueilEtat as EtatRecueil)
            : ReferenceDataService.mapRecueilState(current?.recueilEtat as EtatRecueil);
          doc.dateReference = (useVariantStatus ? signedFileDate(ids) : undefined) ?? current?.recueilReferenceDate;
          if (this.isCouple() && doc.investisseurIds?.length === 1) {
            doc.disabledInvestisseurId = isInv1 ? this.investisseur2Id()! : this.investisseur1Id()!;
          }
          break;
        }
        case GedDocumentType.ProfInvest:
          doc.stepStatus = ReferenceDataService.mapQpiState(current?.qpiEtat as EtatQpi);
          doc.dateReference = current?.qpiReferenceDate;
          doc.disabledInvestisseurId = isInv1 ? this.investisseur2Id()! : this.investisseur1Id()!;
          break;
      }
    }

    // Remove signing docs
    allDocuments = allDocuments.filter(d => d.stepStatus !== StepStatusEnum.Signing);

    // Un profil investisseur réalisé dans une version précédente du questionnaire n'est pas proposé à la signature :
    // il doit être recommencé depuis l'étape Profil investisseur
    const qpiObsolete = {
      inv: invQPI?.versionProfilObsolete === true,
      conjoint: conjointQPI?.versionProfilObsolete === true,
    };
    allDocuments = allDocuments.filter(
      d =>
        d.documentType !== GedDocumentType.ProfInvest ||
        !(
          (qpiObsolete.inv && d.investisseurIds?.includes(this.investisseur1Id())) ||
          (qpiObsolete.conjoint && d.investisseurIds?.includes(this.investisseur2Id()))
        )
    );

    // Build rules for docs to load
    const rules: DocumentLoadingRule[] = [
      // DER
      {
        shouldLoad: () => isIn(inv?.derEtat, [EtatDer.ASigner, EtatDer.Signe]),
        load: async () => {
          const der = await this.derService.loadUnsignedDocument();
          return der?.fileDocument ?? null;
        },
        shouldGenerateFakeDocument: () => inv?.derEtat === EtatDer.ACompleter,
        generateFakeDocument: () => this.derService.getFakeDocument(),
        documentType: GedDocumentType.Der,
        stepStatus: ReferenceDataService.mapDERState(inv?.derEtat as EtatDer),
        dateReference: inv?.derReferenceDate,
        investisseurId: this.investisseur1Id(),
      },
      // RECUEIL
      {
        shouldLoad: () =>
          isIn(inv?.recueilEtat, [EtatRecueil.ASigner, EtatRecueil.Signe, EtatRecueil.Expire]) &&
          !flags.inv.derToComplete &&
          !flags.inv.recueilToComplete,
        load: () => this.recueilService.loadUnsignedDocument(),
        shouldGenerateFakeDocument: () =>
          flags.inv.recueilToComplete ||
          (flags.inv.derToComplete && isIn(inv?.recueilEtat, [EtatRecueil.ASigner, EtatRecueil.Signe, EtatRecueil.Expire])),
        generateFakeDocument: () => this.recueilService.getFakeDocument(),
        documentType: GedDocumentType.RecInfo,
        stepStatus:
          splitRecueilByInvestisseur && this.isCouple()
            ? recueilVariantStatus(this.coupleIds(), inv?.recueilEtat)
            : ReferenceDataService.mapRecueilState(inv?.recueilEtat as EtatRecueil),
        dateReference:
          (splitRecueilByInvestisseur && this.isCouple() ? signedFileDate(this.coupleIds()) : undefined) ?? inv?.recueilReferenceDate,
        investisseurId: this.investisseur1Id(),
      },
      // RECUEIL solo inv1
      {
        shouldLoad: () =>
          splitRecueilByInvestisseur &&
          this.isCouple() &&
          isIn(inv?.recueilEtat, [EtatRecueil.ASigner, EtatRecueil.Signe, EtatRecueil.Expire]) &&
          !flags.inv.derToComplete &&
          !flags.inv.recueilToComplete,
        load: () => this.recueilService.LoadInvestisseurDocument(this.investisseurService.investisseurSig() as Investisseur),
        shouldGenerateFakeDocument: () => false,
        generateFakeDocument: () => this.recueilService.getFakeDocument(),
        documentType: GedDocumentType.RecInfo,
        stepStatus: recueilVariantStatus([this.investisseur1Id()], inv?.recueilEtat),
        dateReference: signedFileDate([this.investisseur1Id()]),
        investisseurId: this.investisseur1Id(),
        investisseurIdToDisable: this.investisseur2Id(),
      },
      // RECUEIL solo inv2
      {
        shouldLoad: () =>
          splitRecueilByInvestisseur &&
          this.isCouple() &&
          isIn(conjoint?.recueilEtat, [EtatRecueil.ASigner, EtatRecueil.Signe, EtatRecueil.Expire]) &&
          !flags.conjoint.derToComplete &&
          !flags.conjoint.recueilToComplete,
        load: () => this.recueilService.LoadInvestisseurDocument(this.investisseurService.investisseurConjointSig() as Investisseur),
        shouldGenerateFakeDocument: () => false,
        generateFakeDocument: () => this.recueilService.getFakeDocument(),
        documentType: GedDocumentType.RecInfo,
        stepStatus: recueilVariantStatus([this.investisseur2Id()], conjoint?.recueilEtat),
        dateReference: signedFileDate([this.investisseur2Id()]),
        investisseurId: this.investisseur2Id(),
        investisseurIdToDisable: this.investisseur1Id(),
      },
      // QPI inv1
      {
        shouldLoad: () =>
          isIn(inv?.qpiEtat, [EtatQpi.ASigner, EtatQpi.SigneNouveauASigner, EtatQpi.Signe, EtatQpi.Expire]) &&
          !flags.inv.derToComplete &&
          (!flags.inv.recueilToComplete || flags.inv.qpiAlreadySigned) &&
          invQPI?.isImportedFromAriane !== true &&
          !qpiObsolete.inv,
        load: () => this.qpiService.loadUnsignedDocument(this.investisseur1Id()),
        shouldGenerateFakeDocument: () =>
          !qpiObsolete.inv &&
          (isIn(inv?.qpiEtat, [EtatQpi.ACompleter, EtatQpi.SigneNouveauACompleter]) ||
            ((flags.inv.derToComplete || (flags.inv.recueilToComplete && !flags.inv.qpiAlreadySigned)) &&
              isIn(inv?.qpiEtat, [EtatQpi.ASigner, EtatQpi.SigneNouveauASigner, EtatQpi.Signe, EtatQpi.Expire]))),
        generateFakeDocument: () => this.qpiService.getFakeDocument(),
        documentType: GedDocumentType.ProfInvest,
        stepStatus: ReferenceDataService.mapQpiState(inv?.qpiEtat as EtatQpi),
        dateReference: inv?.qpiReferenceDate,
        investisseurId: this.investisseur1Id(),
        disabledIfSigned: flags.inv.qpiAlreadySigned,
      },

      // QPI inv2
      {
        shouldLoad: () =>
          isIn(conjoint?.qpiEtat, [EtatQpi.ASigner, EtatQpi.SigneNouveauASigner, EtatQpi.Signe, EtatQpi.Expire]) &&
          !flags.conjoint.derToComplete &&
          (!flags.conjoint.recueilToComplete || flags.conjoint.qpiAlreadySigned) &&
          conjointQPI?.isImportedFromAriane !== true &&
          !qpiObsolete.conjoint,
        load: () => this.qpiService.loadUnsignedDocument(this.investisseur2Id()),
        shouldGenerateFakeDocument: () => false,
        generateFakeDocument: () => this.qpiService.getFakeDocument(),
        documentType: GedDocumentType.ProfInvest,
        stepStatus: ReferenceDataService.mapQpiState(conjoint?.qpiEtat as EtatQpi),
        dateReference: conjoint?.qpiReferenceDate,
        investisseurId: this.investisseur2Id(),
        disabledIfSigned: flags.conjoint.qpiAlreadySigned,
      },
    ];

    // Build lazy placeholders instead of generating every applicable document up front:
    // generation is deferred to generateDocument(), triggered on demand (preview click / send click).
    this.pendingRules.clear();
    const pendingDocuments = rules
      .filter(
        r =>
          r.shouldLoad() &&
          !allDocuments.some(d => d.documentType === r.documentType && r.investisseurId && d.investisseurIds?.includes(r.investisseurId))
      )
      .map(r => {
        const key = `pending-${this.pendingRuleSequence++}`;
        this.pendingRules.set(key, r);
        return this.buildPendingDocument(r, key);
      });

    allDocuments.push(...pendingDocuments);

    // sort documents
    const order = [GedDocumentType.Der, GedDocumentType.RecInfo, GedDocumentType.ProfInvest];
    allDocuments = sortByCustomOrder(allDocuments, order, 'documentType');

    // generate fake documents
    const fakeDocuments = rules
      .filter(
        r =>
          r.shouldGenerateFakeDocument() &&
          !allDocuments.some(d => d.documentType === r.documentType && r.investisseurId && d.investisseurIds?.includes(r.investisseurId))
      )
      .map(r => {
        const f = r.generateFakeDocument();
        return f ? this.buildDoc(f, r) : null;
      })
      .filter((d): d is FileDocument => d !== null);

    allDocuments.push(...fakeDocuments);

    return allDocuments;
  }

  /**
   * Actually generates a previously-deferred document (called on preview click or send click).
   */
  public async generateDocument(generationKey: string): Promise<FileDocument | null> {
    const rule = this.pendingRules.get(generationKey);
    if (!rule) return null;

    const f = await rule.load();
    if (!f) return null;

    this.pendingRules.delete(generationKey);
    return this.buildDoc(f, rule);
  }

  private buildDoc(f: FileDocument, r: DocumentLoadingRule): FileDocument {
    return {
      ...f,
      stepStatus: r.stepStatus,
      disabledIfSigned: r.disabledIfSigned ?? f.disabledIfSigned,
      disabledInvestisseurId: r.investisseurIdToDisable,
      dateReference: r.dateReference ?? f.dateReference,
    };
  }

  private buildPendingDocument(r: DocumentLoadingRule, generationKey: string): FileDocument {
    const base = r.generateFakeDocument();

    // DER and the "couple" Recueil row are shared documents: base.fileName already combines both
    // investisseurs' names (see DERStepService.getFileName()/RecueilService.buildRecueilFileName()).
    // Only the per-investisseur variants (Recueil solo, QPI) need a single-investisseur name here,
    // since their own generateFakeDocument() would otherwise return the couple-combined (Recueil)
    // or always-primary-investisseur (QPI) name.
    const isSoloVariant = !!r.investisseurIdToDisable;
    const isQpi = r.documentType === GedDocumentType.ProfInvest;
    let fileName = base.fileName;
    if (isSoloVariant || isQpi) {
      const investisseur =
        r.investisseurId === this.investisseur2Id()
          ? this.investisseurService.investisseurConjointSig()
          : this.investisseurService.investisseurSig();
      const personne = investisseur?.personnePhysique?.[0];
      fileName = personne ? `${base.description}-${personne.nom?.trim()}_${personne.prenom?.trim()}` : base.fileName;
    }

    const doc = new FileDocument(fileName, new Date(), r.documentType, undefined, base.description);
    doc.isFakeFile = false;
    doc.isPendingGeneration = true;
    doc.generationKey = generationKey;
    // DER and the "couple" Recueil row will, once generated, carry both investisseurs' ids (see
    // DERStepService.loadUnsignedDocument()/RecueilService.loadUnsignedDocument()) — the placeholder must
    // match that shape, otherwise couple-vs-solo Recueil row detection (investisseurIds.length === 2)
    // elsewhere in the dialog breaks.
    doc.investisseurIds =
      isSoloVariant || isQpi ? (r.investisseurId ? [r.investisseurId] : undefined) : this.investisseurService.coupleInvestisseurIds();

    return this.buildDoc(doc, r);
  }

  private async ensureQPIStateBeforeLoadingDocuments(): Promise<void> {
    const investisseur = this.investisseurService.investisseurSig();
    if (!investisseur?.id) {
      return;
    }

    const avancement = investisseur.avancementConnaissanceClientInvestisseur;
    const qpiResults = await this.qpiService.getQPIResults(investisseur.id);
    // Condition isImportedFromAriane à rajouter pour éviter de recalculer la CSP si le QPI a été importé depuis Ariane et que le profil de risque est déjà à jour (Signe)
    if (
      !avancement?.qpiEtat ||
      ![EtatQpi.ASigner, EtatQpi.SigneNouveauASigner, EtatQpi.Signe].includes(avancement.qpiEtat) ||
      (qpiResults?.isImportedFromAriane === true && avancement.qpiEtat === EtatQpi.Signe)
    ) {
      return;
    }

    // Produits du recueil sélectionnés automatiquement depuis la dernière saisie (questionnaire Connaissance et expérience)
    if (qpiResults?.isVersionProfilCourante) {
      await this.qpiService.synchroniserSelectionAutoEtNotifier(investisseur.id);
    }

    await this.qpiService.recalculateCspAndNotifyChanges(investisseur.id, qpiResults?.resultatCapacitePerte);
  }

  //#endregion

  //#region Form validation rules

  public checkFormValid(form: FormGroup<SignatureForm>): boolean {
    this.errorTooltip.set('');

    const signataires = form.controls.signataires;
    const documents = form.controls.documents;

    const activeDocs = documents.controls.filter(d => d.valid && d.value.isActive);
    const signataireIds = this.getSelectedSignataireIds(activeDocs);

    if (activeDocs.length === 0) {
      this.errorTooltip.set(`Vous devez sélectionner au moins un document`);
      return false;
    }

    if (!this.allSelectedDocsHaveInvestisseur(activeDocs, signataires)) {
      this.errorTooltip.set(`Vous devez sélectionner au moins un investisseur pour chaque document`);
      return false;
    }

    if (this.hasDocsWithSameName(activeDocs)) {
      this.errorTooltip.set(`Vous ne pouvez pas sélectionner plusieurs documents avec le même nom`);
      return false;
    }

    if (!this.areSelectedSignatairesValid(signataires, signataireIds)) {
      this.errorTooltip.set(`Les signataires doivent avoir un email et mobile valides`);
      return false;
    }

    const isInvestisseursValid = this.isCoupleInvestisseurValid(signataires, this.coupleIds(), activeDocs);
    if (!isInvestisseursValid) {
      return false;
    }

    return true;
  }

  private getSelectedSignataireIds(docs: AbstractControl[]): number[] {
    return docs.flatMap(d => {
      const signataires = d.get('signataires') as FormArray<FormGroup<SelectedSignataire>>;
      return signataires.controls.filter(s => s.value.selected).map(s => s.value.signataireId as number);
    });
  }

  private allSelectedDocsHaveInvestisseur(docs: AbstractControl[], signataires: FormArray<FormGroup<SignataireForm>>): boolean {
    const investisseurIds = signataires.value.filter(s => s.signataireType === SignataireType.Investisseur).map(s => s.id) as number[];
    return docs.every(d => this.hasAnySelectedSignataire(d, investisseurIds));
  }

  private hasDocsWithSameName(docs: FormGroup<DocumentForm>[]): boolean {
    const names = docs.filter(d => d.value.signataires?.some(s => s.selected)).map(d => d.value.filename);
    const uniqueNames = new Set(names);
    return uniqueNames.size !== names.length;
  }

  private areSelectedSignatairesValid(signataires: FormArray<FormGroup<SignataireForm>>, selectedIds: number[]): boolean {
    return signataires.controls.filter(s => selectedIds.includes(s.value.id!)).every(s => s.valid || !s.value.isActive);
  }

  private isCoupleInvestisseurValid(
    signataires: FormArray<FormGroup<SignataireForm>>,
    investisseurIds: number[],
    activeDocs: AbstractControl[]
  ): boolean {
    const grouped = this.groupByInvestisseur(signataires);

    return investisseurIds.every(investisseurId => {
      const group = grouped.get(investisseurId) ?? [];

      if (group.length === 0) return true;

      const hasSelection = this.groupHasSelection(group, activeDocs);

      if (!hasSelection) return true;

      const signataire = signataires.controls.find(s => s.value.id === investisseurId);
      return group.some(() => this.isInvestisseurFlowValid(signataire, group, activeDocs));
    });
  }

  private groupByInvestisseur(signataires: FormArray<FormGroup<SignataireForm>>): Map<number, FormGroup<SignataireForm>[]> {
    const map = new Map<number, FormGroup<SignataireForm>[]>();

    signataires.controls.forEach(s => {
      const value = s.value;

      if (value.signataireType === SignataireType.Investisseur) {
        const investisseurId = value.id!;

        if (!map.has(investisseurId)) {
          map.set(investisseurId, []);
        }

        map.get(investisseurId)!.push(s);
      }

      if (value.representantDeIds?.length) {
        value.representantDeIds.forEach(investisseurId => {
          if (!map.has(investisseurId)) {
            map.set(investisseurId, []);
          }

          map.get(investisseurId)!.push(s);
        });
      }
    });

    return map;
  }

  private groupHasSelection(group: FormGroup<SignataireForm>[], docs: AbstractControl[]): boolean {
    const groupIds = group.map(s => s.value.id);

    return docs.some(d => {
      const signataires = d.get('signataires') as FormArray<FormGroup<SelectedSignataire>>;

      return signataires.controls.some(s => s.value.selected && groupIds.includes(s.value.signataireId));
    });
  }

  private isInvestisseurFlowValid(
    investisseur: FormGroup<SignataireForm> | undefined,
    signatairesGroup: FormGroup<SignataireForm>[],
    activeDocs: AbstractControl[]
  ): boolean {
    if (!investisseur) return true;

    const derSelected = this.isDocSelectedForGroup(activeDocs, GedDocumentType.Der, signatairesGroup);
    const recueilSelected = this.isDocSelectedForGroup(activeDocs, GedDocumentType.RecInfo, signatairesGroup);
    const profilSelected = this.isDocSelectedForGroup(activeDocs, GedDocumentType.ProfInvest, signatairesGroup);

    const derSigned = this.isDERSignedOrSigning(investisseur);
    const recueilSigned = this.isRecueilSignedOrSigning(investisseur);
    const qpiAlreadySigned = investisseur.value.qpiEtat
      ? [EtatQpi.Signe, EtatQpi.SigneNouveauASigner].includes(investisseur.value.qpiEtat)
      : false;

    const derOk = derSigned || derSelected;
    const recueilOk = recueilSigned || (recueilSelected && investisseur.value.recueilFormComplete);

    if (recueilSelected && !derOk) {
      this.errorTooltip.set(`L'envoi en signature du Recueil d'informations nécessite d'inclure le DER (si son statut est « À signer »)`);
      return false;
    }

    if (profilSelected && (!derOk || (!recueilOk && !qpiAlreadySigned))) {
      this.errorTooltip.set(
        `L'envoi en signature du Profil investisseur nécessite d'inclure le DER et le Recueil d'informations (si leur statut est « À signer »)`
      );
      return false;
    }

    return true;
  }

  private isDocSelectedForGroup(docs: AbstractControl[], type: GedDocumentType, signatairesGroup: FormGroup<SignataireForm>[]): boolean {
    const groupIds = signatairesGroup.map(s => s.value.id);

    return docs
      .filter(d => d.value.documentType === type)
      .some(d => {
        const signataires = d.get('signataires') as FormArray<FormGroup<SelectedSignataire>>;

        return signataires.controls.some(s => s.value.selected && groupIds.includes(s.value.signataireId));
      });
  }

  private hasAnySelectedSignataire(doc: AbstractControl, investisseurIds: number[]): boolean {
    const signataires = doc.get('signataires') as FormArray<FormGroup<SelectedSignataire>>;
    // si le document est sélectionné, il doit y avoir au moins un signataire sélectionné qui correspond à l'investisseur
    return signataires.controls.some(s => s.value.selected && investisseurIds.includes(s.value.signataireId ?? 0));
  }

  private isDERSignedOrSigning(investisseurToCheck: FormGroup<SignataireForm>): boolean {
    const derEtat = investisseurToCheck.value.derEtat;
    return derEtat ? [EtatDer.Signe, EtatDer.EnSignature, EtatDer.EnSignatureApresSigne].includes(derEtat) : false;
  }

  private isRecueilSignedOrSigning(investisseurToCheck: FormGroup<SignataireForm>): boolean {
    const recueilEtat = investisseurToCheck.value.recueilEtat;
    return recueilEtat
      ? [
          EtatRecueil.Signe,
          EtatRecueil.EnSignaturePremierFois,
          EtatRecueil.EnSignatureApresExpiration,
          EtatRecueil.EnSignatureApresSigne,
        ].includes(recueilEtat)
      : false;
  }

  //#endregion
}
